/**
 * PasskeySettings Component
 * Allows users to register and manage their passkeys for biometric login
 */
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useWebAuthn, isWebAuthnSupported } from '../hooks/useWebAuthn';
import {
  Fingerprint,
  Plus,
  Trash2,
  Loader2,
  Shield,
  Smartphone,
  Clock,
  AlertCircle
} from 'lucide-react';

const PasskeySettings = () => {
  const {
    isLoading,
    credentials,
    isSupported,
    fetchCredentials,
    registerPasskey,
    deleteCredential
  } = useWebAuthn();
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [passkeyName, setPasskeyName] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (isSupported) {
      fetchCredentials();
    }
  }, [isSupported, fetchCredentials]);

  const handleRegister = async () => {
    const name = passkeyName.trim() || `Passkey ${new Date().toLocaleDateString()}`;
    const success = await registerPasskey(name);
    if (success) {
      setShowAddModal(false);
      setPasskeyName('');
    }
  };

  const handleDelete = async (credentialId) => {
    if (!confirm('Are you sure you want to delete this passkey? You won\'t be able to use it to log in anymore.')) {
      return;
    }
    setDeletingId(credentialId);
    await deleteCredential(credentialId);
    setDeletingId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isSupported) {
    return (
      <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <Fingerprint className="h-6 w-6 text-slate-500" />
          <h3 className="text-lg font-semibold text-white">Passkey Login</h3>
        </div>
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <p className="text-amber-500 font-medium">Not Supported</p>
            <p className="text-sm text-slate-400 mt-1">
              Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, or Edge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Fingerprint className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Passkey Login</h3>
            <p className="text-sm text-slate-400">Use fingerprint or face recognition to sign in</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          disabled={isLoading}
          className="bg-blue-500 hover:bg-blue-400"
          data-testid="add-passkey-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Passkey
        </Button>
      </div>

      {/* Passkey List */}
      <div className="space-y-3">
        {credentials.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-slate-700 rounded-lg">
            <Shield className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No passkeys registered yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Add a passkey to enable fingerprint or face recognition login
            </p>
          </div>
        ) : (
          credentials.map((cred) => (
            <div
              key={cred.credential_id}
              className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-lg"
              data-testid={`passkey-item-${cred.credential_id}`}
            >
              <div className="flex items-center gap-4">
                <div className="p-2 bg-slate-700 rounded-lg">
                  <Smartphone className="h-5 w-5 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-white">{cred.device_name}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Created: {formatDate(cred.created_at)}
                    </span>
                    {cred.last_used && (
                      <span>Last used: {formatDate(cred.last_used)}</span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(cred.credential_id)}
                disabled={deletingId === cred.credential_id}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                data-testid={`delete-passkey-${cred.credential_id}`}
              >
                {deletingId === cred.credential_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 bg-slate-800/30 border border-slate-700 rounded-lg">
        <h4 className="text-sm font-medium text-slate-300 mb-2">How passkeys work</h4>
        <ul className="text-xs text-slate-500 space-y-1">
          <li>• Passkeys use your device's built-in security (fingerprint, face, or PIN)</li>
          <li>• Your biometric data never leaves your device</li>
          <li>• Each passkey is unique to this account and device</li>
          <li>• You can register multiple passkeys for different devices</li>
        </ul>
      </div>

      {/* Add Passkey Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Fingerprint className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Register Passkey</h3>
                <p className="text-sm text-slate-400">Add a new passkey to your account</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">
                  Passkey Name (optional)
                </label>
                <Input
                  value={passkeyName}
                  onChange={(e) => setPasskeyName(e.target.value)}
                  placeholder="e.g., MacBook Pro, iPhone 15"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="passkey-name-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Give this passkey a name to help you identify it later
                </p>
              </div>

              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  When you click "Register", your browser will prompt you to use your device's biometric authentication.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  setPasskeyName('');
                }}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRegister}
                disabled={isLoading}
                className="bg-blue-500 hover:bg-blue-400"
                data-testid="confirm-register-passkey"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Fingerprint className="h-4 w-4 mr-2" />
                    Register Passkey
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasskeySettings;
